import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Building2, Users, CreditCard, Settings, Bell, Wallet,
  LogOut, ChevronDown, ChevronLeft, Menu, X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LOGO_URL } from '../config/brand';

const NAV = [
  { id: 'dashboard',     label: 'Dashboard',     icon: LayoutDashboard, to: '/dashboard' },
  { id: 'organizations', label: 'Organizations', icon: Building2,        to: '/organizations' },
  { id: 'users',         label: 'Users',         icon: Users,           to: '/users' },
  { id: 'billing',       label: 'Billing',       icon: CreditCard,      to: '/billing' },
  { id: 'accounts',      label: 'Accounts',      icon: Wallet,          to: '/accounts' },
  { id: 'notifications', label: 'Notifications', icon: Bell,            to: '/notifications' },
  { id: 'settings',      label: 'Settings',      icon: Settings,        to: '/settings' },
];

function NavItem({ item, collapsed }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/organizations' ? false : undefined}
      className={({ isActive }) => [
        'relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150',
        isActive
          ? 'text-ink bg-gradient-to-r from-[rgba(59,130,246,0.14)] to-[rgba(59,130,246,0.05)] side-active-bar'
          : 'text-muted hover:text-ink-2 hover:bg-white/[0.03]',
        collapsed ? 'justify-center px-3' : '',
      ].join(' ')}
    >
      {({ isActive }) => (
        <>
          <Icon size={16} className={isActive ? 'text-accent' : 'text-muted'} strokeWidth={2} />
          {!collapsed && <span>{item.label}</span>}
        </>
      )}
    </NavLink>
  );
}

export default function Sidebar({ collapsed = false, onToggle }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const initials = [user?.first_name, user?.last_name].filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'AD';

  const Content = ({ collapsed }) => (
    <div className={['flex flex-col h-full bg-gradient-to-b from-[#141417] to-bg border-r border-border relative', collapsed ? 'items-center py-5 px-3' : 'py-5 px-3.5'].join(' ')}>
      <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-[rgba(59,130,246,0.3)] to-transparent pointer-events-none" />

      {/* Brand */}
      <div className={['flex items-center gap-2.5 px-2 mb-1', collapsed ? 'justify-center' : ''].join(' ')}>
        <img src={LOGO_URL} alt="NexCarat" className="w-10 h-10 object-contain flex-shrink-0" />
        {!collapsed && (
          <div className="flex flex-col leading-tight">
            <span className="font-display text-[19px] font-bold tracking-tight text-white whitespace-nowrap">NexCarat</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-accent">Admin Console</span>
          </div>
        )}
      </div>

      {/* Collapse toggle (desktop) */}
      {onToggle && (
        <button onClick={onToggle} className="hidden md:flex items-center justify-center absolute -right-3 top-6 w-6 h-6 rounded-full bg-surface border border-border-2 text-muted hover:text-ink z-10" title={collapsed ? 'Expand' : 'Collapse'}>
          <ChevronLeft size={13} style={{ transition: 'transform .2s', transform: collapsed ? 'rotate(180deg)' : 'none' }} />
        </button>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto flex flex-col gap-0.5 mt-5 scrollbar-none">
        {!collapsed && <span className="font-mono text-[10px] text-muted-2 uppercase tracking-[0.14em] px-3 pb-1">Platform</span>}
        {NAV.map(item => <NavItem key={item.id} item={item} collapsed={collapsed} />)}
      </nav>

      {/* User footer */}
      <div className="mt-3.5 pt-3.5 border-t border-border relative">
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-[70]" onClick={() => setMenuOpen(false)} />
            <div className="absolute bottom-[calc(100%+6px)] left-0 right-0 z-[80] bg-surface border border-border-2 rounded-[14px] overflow-hidden shadow-[0_16px_40px_-8px_rgba(0,0,0,0.75)]" style={{ minWidth: collapsed ? 190 : undefined }}>
              <button onClick={() => { setMenuOpen(false); logout(); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#EF4444] hover:bg-[rgba(239,68,68,0.08)] transition-colors text-left" style={{ border: 0, cursor: 'pointer', background: 'transparent' }}>
                <LogOut size={16} className="flex-shrink-0" strokeWidth={1.75} />
                <span>Sign Out</span>
              </button>
            </div>
          </>
        )}
        <button onClick={() => setMenuOpen(o => !o)} className={['w-full flex items-center gap-2.5 px-2.5 py-2 bg-surface border rounded-[10px] hover:border-[rgba(59,130,246,0.4)] hover:bg-surface-2 transition-all', menuOpen ? 'border-[rgba(59,130,246,0.4)]' : 'border-border', collapsed ? 'justify-center' : ''].join(' ')}>
          <div className="w-8 h-8 gold-grad rounded-full flex items-center justify-center text-on-accent text-xs font-bold flex-shrink-0">{initials}</div>
          {!collapsed && (
            <>
              <div className="flex flex-col flex-1 min-w-0 text-left">
                <span className="text-sm font-semibold text-ink truncate">{[user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Admin'}</span>
                <span className="font-mono text-[10px] text-muted uppercase">{user?.role?.replace('_', ' ') || 'admin'}</span>
              </div>
              <ChevronDown size={14} className="text-muted flex-shrink-0" style={{ transition: 'transform .2s', transform: menuOpen ? 'rotate(180deg)' : 'none' }} />
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="md:hidden flex items-center justify-between px-4 h-14 bg-surface border-b border-border sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="NexCarat" className="w-7 h-7 object-contain" />
          <span className="font-display font-bold text-ink">NexCarat <span className="text-accent text-xs">Admin</span></span>
        </div>
        <button onClick={() => setMobileOpen(true)} className="text-ink"><Menu size={22} /></button>
      </div>

      {/* Desktop sidebar */}
      <aside className={['hidden md:block fixed left-0 top-0 h-screen z-40 transition-[width] duration-200', collapsed ? 'w-[76px]' : 'w-[248px]'].join(' ')}>
        <Content collapsed={collapsed} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[248px]">
            <button onClick={() => setMobileOpen(false)} className="absolute right-3 top-4 z-10 text-muted hover:text-ink"><X size={20} /></button>
            <Content collapsed={false} />
          </div>
        </div>
      )}
    </>
  );
}
